import React, {useEffect, useRef, useState} from "react";
import Rest from "../../../../helpers/Rest";
import {statuses} from "../../Config"
import BatteryStdIcon from '@material-ui/icons/BatteryStd';
import ProgressOr from "../../../../components/ProgressOr/ProgressOr";
import {Grid, Typography, MenuItem, useTheme, Box, IconButton} from "@material-ui/core";
import {SaveAlt} from "@material-ui/icons";
import Widget from "../../../../components/Widget/Widget";
import {DateFormat} from "../../../../helpers/DateFormat";
import useStyles from "./styles"
import {
    DEFAULT_SELECTOR_VALUE,
    filter,
    getDateFilterClause,
    getFilterClause,
    SonaSafeDateSelectorField,
    SonaSafeSelectorField
} from "../../components/Selectors/Selectors";
import TypeSelector from "./TypeSelector";

const STATE = 0, SW_VERSION = 1, HW_VERSION = 2,
    BATTERY_LEVEL = 3, OPERATION_MODE = 4,
    TEST_RESULT = 5, RF_CHANNEL = 6, MESSAGE = 7,
    DIAGNOSTICS = 8, TIMESTAMP = 9, FAULT_DESCRIPTION = 10,
    DEFAULT_STATUS = ['uninstalled', '', '', 0, '', '', '', '', '', '', '']

const POLL_INTERVAL = 60000;

class Arc{
    constructor(size, strokeWidth){
        this.size = size
        this.strokeWidth = strokeWidth
        this.radius = (size - strokeWidth) * 0.5
    }

    xy(ratio) {
        if (ratio > 1.0) {
            return '1 1';
        }
        return '0 1';
    }

    pointAt(ratio) {
        const angle = Math.PI * ratio;
        const offset = this.size * 0.5
        return ((Math.cos(angle) * this.radius) + offset) + ' ' + ((Math.sin(angle) * this.radius) + offset);
    }

    getPath(ratio){
        const fixed = ratio === 1.0 ? 1.99 : 2 * ratio;
        return `M ${this.pointAt(0)} A ${this.radius} ${this.radius} 0 ${this.xy(fixed)} ${this.pointAt(fixed)}`
    }
}
const ARC = new Arc(100, 8)

function appendDiagnosticFields(status, fields){
    const diog = status[DIAGNOSTICS]
    if (!diog){
        return fields
    }
    const keys = Object.keys(diog)
    for (let i = 0, key = keys[i]; key; key = keys[++i]){
        const value = diog[key]
        fields.push(key)
        fields.push((typeof value) === 'object' ? JSON.stringify(value) : value)
    }
    return fields
}

function colourFromHex(r, g, b) {
    return [
        parseInt(r, 16),
        parseInt(g, 16),
        parseInt(b, 16)
    ]
}

function splitColour(colour) {
    let match = /#(.)(.)(.)/.exec(colour)
    if (match){
        return colourFromHex(match[1] + match[1], match[2] + match[2], match[3] + match[3])
    }
    match = /#(.{2})(.{2})(.{2})/.exec(colour)
    if (match){
        return colourFromHex(match[1], match[2], match[3])
    }
    match = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(colour)
    return match ? [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])] : [0, 0, 0]
}

function toHex(num){
    const val = Math.round(num).toString(16)
    return val.length === 1 ? '0' + val : val
}

function saturate(colour, saturation) {
    const mul = saturation / Math.max(...colour)
    for (let i = 0; i < 3; ++i){
        colour[i] *= mul
    }
    return colour
}

function lerpColour(a, b, c) {
    a = splitColour(a)
    b = splitColour(b)
    const inv = 1 - c
    const saturation = Math.max(...a) * inv + Math.max(...b) * c
    for (let i = 0; i < 3; ++i){
        a[i] = a[i] * inv + b[i] * c
    }
    return saturate(a, saturation).reduce((accum, val) => {
        return accum + toHex(val)
    }, '#')
}

function Column({children, classes}){
    const len = children.length
    const items = new Array(len)
    for (let i = 0; i < len; ++i){
        items[i] = (
            <Grid item md={4} xs={6} key={i}><b>{children[i]}</b></Grid>
        )
        ++i
        items[i] = (
            <Grid item md={8} xs={6} key={i}>{children[i]}</Grid>
        )
    }
    return <Grid container spacing={1} item xs={12} sm={6} className={classes.propertyTable}>
        {items}
    </Grid>
}

function PropertyTable({status, classes}){
    const messages = status[FAULT_DESCRIPTION]
    return <Widget disableWidgetMenu>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h4">
                    {(new DateFormat(status[TIMESTAMP])).datetime()}
                </Typography>
            </Grid>
            <Column classes={classes}>{[
                "State", status[STATE],
                "Software", status[SW_VERSION],
                "Hardware", status[HW_VERSION],
                "Battery", status[BATTERY_LEVEL],
                "Result", status[TEST_RESULT],
                "Messages", messages && messages.length ? messages.split(";").map(part => <>{part}<br/></>) : "None"
            ]}</Column>
            <Column classes={classes}>{
                appendDiagnosticFields(
                    status, [
                        "RF Channel", status[RF_CHANNEL],
                        "Operation Mode", status[OPERATION_MODE]
                    ]
                ).concat(["Message", status[MESSAGE]])
            }</Column>
        </Grid>
    </Widget>
}

function Filters({items, onFilter}){
    const [dates, setDates] = useState(null)
    const [status, setStatus] = useState(DEFAULT_SELECTOR_VALUE)
    const [date, setDate] = useState(DEFAULT_SELECTOR_VALUE)

    useEffect(() => {
        setDates(items.map(item => item[TIMESTAMP]))
    }, [items])

    useEffect(() => {
        onFilter(filter([
            getFilterClause(STATE, status),
            getDateFilterClause(TIMESTAMP, date)
        ], items))
    }, [items, date, status]);

    return <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
            <SonaSafeSelectorField value={status} onChange={e => setStatus(e.target.value)}>
                {Object.keys(statuses).map(key => (
                    <MenuItem key={key} value={key}>{statuses[key].label}</MenuItem>
                ))}
            </SonaSafeSelectorField>
        </Grid>
        <Grid item xs={12} sm={6}>
            <SonaSafeDateSelectorField
                dates={dates || []}
                value={date}
                onChange={e => setDate(e.target.value)}
            />
        </Grid>
    </Grid>
}

function PropertyCell({heading, children, className}){
    return <Grid item md={2} sm={4} xs={6} className={className}>
        <b>
            {children}
        </b>
        <p>
            {heading}
        </p>
    </Grid>
}

function BatteryIndicator({level, classes}){
    const colour = lerpColour(statuses.faulty.colour, statuses.healthy.colour, level)
    const centre = ARC.size * 0.5
    return <Box className={classes.batteryIndicator}>
        <BatteryStdIcon style={{color:colour}}/>
        <Box>
            <svg viewBox={`0 0 ${ARC.size} ${ARC.size}`}>
                <path d={ARC.getPath(level)}
                      fill="none"
                      stroke={colour}
                      strokeWidth={ARC.strokeWidth}
                      strokeDasharray={`${ARC.strokeWidth} ${ARC.strokeWidth}`}
                      transform={`rotate(270 ${centre} ${centre})`}
                />
            </svg>
        </Box>
    </Box>
}

function CurrentStatus({classes, status, device}){
    const state = statuses[status[STATE]]
    return <Widget className={classes.currentStatus}>
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h4">
                    Current Status
                </Typography>
            </Grid>
            <Grid container item xs={12} spacing={0} justify="center">
                <Grid item md={10} sm={8} xs={6}>
                    <b style={{color: state.colour}}>
                        {state.label}
                    </b>
                </Grid>
                <Grid item md={2} sm={4} xs={6}>
                    <BatteryIndicator level={status[BATTERY_LEVEL] * 0.01} classes={classes}/>
                </Grid>
            </Grid>
            <Grid container item xs={12} spacing={0} className={classes.properties}>
                <PropertyCell heading="Latest SW Version">{device.sw_version}</PropertyCell>
                <PropertyCell heading="Latest HW Version">{device.hw_version}</PropertyCell>
                <PropertyCell heading="Last Report">{new DateFormat(status[TIMESTAMP]).datetime()}</PropertyCell>
                <PropertyCell heading="Test Result">{status[TEST_RESULT]}</PropertyCell>
                <PropertyCell heading="RF Channel">{status[RF_CHANNEL]}</PropertyCell>
                <PropertyCell heading="Battery" className={classes.battery}>{`%${status[BATTERY_LEVEL]}`}</PropertyCell>
                <Grid item md={2} sm={4} xs={6}>
                    <b className={classes.modeHeading}>Mode</b>
                </Grid>
                <Grid item md={2} sm={4} xs={6}>
                    <b>{status[OPERATION_MODE]}</b>
                </Grid>
            </Grid>
        </Grid>
    </Widget>
}

export default function({device}){
    const classes = useStyles(useTheme())
    const [statuses, setStatuses] = useState(null)
    const [filtered, setFiltered] = useState(null)
    const callback = useRef(null)

    function getStatuses(){
        Rest.get(`device/${device.id}/status/list`).then((reply) => {
            reply.sort((a, b) => {
                return b[TIMESTAMP].localeCompare(a[TIMESTAMP])
            })
            setStatuses(reply)
        })
    }

    useEffect(() => {
        getStatuses()
        callback.current = setInterval(getStatuses, POLL_INTERVAL)
        return () => clearInterval(callback.current)
    }, [device]);

    return <ProgressOr showProgress={!statuses} progressMessage={`Fetching status of ${device.name}...`}>
        <Grid container spacing={2} className={classes.main}>
            <Grid item xs={5} sm={4} md={2}>
                <TypeSelector fullWidth />
            </Grid>
            <Grid item xs={2} md={1}>
                <IconButton onClick={() => Rest.getCsv(
                    `device/${device.id}/status/list/csv`,
                    `status_${device.code}.csv`
                )}>
                    <SaveAlt />
                </IconButton>
            </Grid>
            <Grid item xs={12}>
                <CurrentStatus
                    classes={classes}
                    status={statuses && statuses.length && statuses[0] || DEFAULT_STATUS}
                    device={device}
                />
            </Grid>
            <Grid item xs={12}>
                <Filters items={statuses || []} onFilter={setFiltered} />
            </Grid>
            {filtered && filtered.map((item, idx) => <Grid item xs={12} key={idx}>
                <PropertyTable status={item} classes={classes} />
            </Grid>)}
        </Grid>
    </ProgressOr>
}